import { v4 as uuidv4 } from 'uuid'
import { isNil } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  ProviderFragment,
  ProviderFragmentDoc,
  CreateProviderInput,
  BuildingFragment,
  UpdateProviderInput,
  useCreateProviderMutation,
  useDeleteProviderMutation,
  useUpdateProviderMutation,
  AnyProviderParametersInput,
  ProviderParametersFragment,
  DemandFragmentDoc,
  ProviderDemandInput,
  ProviderDemandFragment,
  DemandFragment,
  HourSeriesFragment,
  HourSeriesFragmentDoc,
  LocalGridFragment,
  PerformanceTableFragment,
  PerformanceTableFragmentDoc,
  TemperatureCurveFragment,
  TemperatureCurveFragmentDoc,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'
import { staticProviderTypes } from './provider-properties'
import { useNotifications } from '../notifications/use-notifications'

const deviceToParametersTypenameMap: Record<
  keyof AnyProviderParametersInput,
  ProviderParametersFragment['__typename']
> = {
  inefficientDevice: 'InefficientParameters',
  heatBoiler: 'HeatBoilerParameters',
  waterCoolingHeatPump: 'WaterCoolingHeatPumpParameters',
  heatingAirHeatPump: 'HeatingAirHeatPumpParameters',
  heatingGroundHeatPump: 'HeatingGroundHeatPumpParameters',
  coolingAirHeatPump: 'CoolingAirHeatPumpParameters',
  coolingGroundHeatPump: 'CoolingGroundHeatPumpParameters',
  circulationHeatPump: 'CirculationHeatPumpParameters',
  hybridHeatPump: 'HybridHeatPumpParameters',
  preheater: 'PreheaterParameters',
  solarPanel: 'SolarPanelParameters',
  battery: 'BatteryParameters',
}

export const convertInputParametersToProviderParameters = (
  inputParameters: AnyProviderParametersInput,
): ProviderParametersFragment => {
  const entries = Object.entries(inputParameters)
  if (entries.length === 0) {
    throw new Error('Unable to convert input parameters to provider parameters')
  }

  const [deviceKey, parameters] = entries[0] as [
    keyof AnyProviderParametersInput,
    typeof entries[0][1],
  ]

  const typename = deviceToParametersTypenameMap[deviceKey]
  if (!typename) {
    throw new Error('Device key to parameters typename mapping not found')
  }

  return {
    __typename: typename,
    isWasteSold: false,
    ...parameters,
  } as ProviderParametersFragment
}

type ProviderMutations = {
  createProvider: (
    building: BuildingFragment | undefined,
    localGrid: LocalGridFragment | undefined,
    input: CreateProviderInput,
  ) => Promise<ProviderFragment>

  updateProvider: (
    id: string,
    input: UpdateProviderInput,
    container: BuildingFragment | LocalGridFragment | null,
  ) => Promise<ProviderFragment>

  deleteProvider: (
    building: BuildingFragment | undefined,
    localGrid: LocalGridFragment | undefined,
    id: string,
  ) => Promise<boolean>
}

export function useProviders(): ProviderMutations {
  const [createProviderMutation] = useCreateProviderMutation()
  const [updateProviderMutation] = useUpdateProviderMutation()
  const [deleteProviderMutation] = useDeleteProviderMutation()
  const { getFragment, addItemToList, removeItemFromList } = useCacheHelpers()
  const { enqueueError } = useNotifications()
  const { t } = useTranslation()

  const convertInputDemandsToProviderDemands = (
    inputDemands: ProviderDemandInput[],
  ): ProviderDemandFragment[] =>
    inputDemands.map((demand) => ({
      __typename: 'ProviderDemand',
      id: uuidv4(),
      demand: getFragment<DemandFragment>(demand.demandId, DemandFragmentDoc),
      maxPortion: demand.maxPortion,
    }))

  const checkProviderName = (
    name: string,
    container: BuildingFragment | LocalGridFragment | undefined,
    id?: string,
  ) => {
    const otherProviderNames =
      container?.providers
        .filter((provider) => provider.id !== id)
        .map((provider) => provider.name) ?? []

    if (otherProviderNames.includes(name)) {
      enqueueError(t('providers.conflictingNameErrorMessage'))

      throw new Error(
        'Provider name already exists, please choose another name.',
      )
    }
  }

  const createProvider = async (
    building: BuildingFragment | undefined,
    localGrid: LocalGridFragment | undefined,
    input: CreateProviderInput,
  ) => {
    checkProviderName(input.name, building ?? localGrid)

    const result = await createProviderMutation({
      variables: {
        buildingId: building?.id,
        localGridId: localGrid?.id,
        input,
      },

      update: (_, mutationResult) => {
        const provider = mutationResult.data?.createProvider
        if (!provider) {
          return
        }

        if (building) {
          addItemToList(ProviderFragmentDoc, provider, building, 'providers')
        }

        if (localGrid) {
          addItemToList(ProviderFragmentDoc, provider, localGrid, 'providers')
        }
      },

      optimisticResponse: {
        createProvider: {
          __typename: 'Provider',
          id: uuidv4(),
          name: input.name,
          type: input.type,
          isFallback: staticProviderTypes.includes(input.type),
          createdAt: new Date().toISOString(),
          hourSeries: input.hourSeriesIds.map((id) =>
            getFragment<HourSeriesFragment>(id, HourSeriesFragmentDoc),
          ),
          performanceTables: input.performanceTableIds.map((id) =>
            getFragment<PerformanceTableFragment>(
              id,
              PerformanceTableFragmentDoc,
            ),
          ),
          temperatureCurves: input.temperatureCurveIds.map((id) =>
            getFragment<TemperatureCurveFragment>(
              id,
              TemperatureCurveFragmentDoc,
            ),
          ),
          parameters: convertInputParametersToProviderParameters(
            input.parameters,
          ),
          demands: convertInputDemandsToProviderDemands(input.demands),
          providesElectricityForProviders:
            input.providesElectricityForProviders,
        },
      },
    })

    if (!result.data) {
      throw new Error('Provider creation failed')
    }

    return result.data.createProvider
  }

  const updateProvider = async (
    id: string,
    input: UpdateProviderInput,
    container: BuildingFragment | LocalGridFragment | null,
  ) => {
    if (input.name && container) {
      checkProviderName(input.name, container, id)
    }

    const provider = getFragment<ProviderFragment>(id, ProviderFragmentDoc)

    const hourSeries = input.hourSeriesIds
      ? input.hourSeriesIds.map((hourSeriesId) =>
          getFragment<HourSeriesFragment>(hourSeriesId, HourSeriesFragmentDoc),
        )
      : provider.hourSeries

    const performanceTables = input.performanceTableIds
      ? input.performanceTableIds.map((tableId) =>
          getFragment<PerformanceTableFragment>(
            tableId,
            PerformanceTableFragmentDoc,
          ),
        )
      : provider.performanceTables

    const temperatureCurves = input.temperatureCurveIds
      ? input.temperatureCurveIds.map((curveId) =>
          getFragment<TemperatureCurveFragment>(
            curveId,
            TemperatureCurveFragmentDoc,
          ),
        )
      : provider.temperatureCurves

    const result = await updateProviderMutation({
      variables: { id, input },

      optimisticResponse: {
        updateProvider: {
          ...provider,
          name: input.name ?? provider.name,
          type: input.type ?? provider.type,
          hourSeries,
          performanceTables,
          temperatureCurves,
          parameters: input.parameters
            ? convertInputParametersToProviderParameters(input.parameters)
            : provider.parameters,
          demands: input.demands
            ? convertInputDemandsToProviderDemands(input.demands)
            : provider.demands,
          providesElectricityForProviders: !isNil(
            input.providesElectricityForProviders,
          )
            ? input.providesElectricityForProviders
            : provider.providesElectricityForProviders,
        },
      },
    })

    if (!result.data) {
      throw new Error('Provider update failed')
    }

    return result.data.updateProvider
  }

  const deleteProvider = async (
    building: BuildingFragment | undefined,
    localGrid: LocalGridFragment | undefined,
    id: string,
  ) => {
    const result = await deleteProviderMutation({
      variables: { id },

      update: () => {
        if (building) {
          removeItemFromList(ProviderFragmentDoc, id, building, 'providers')
        }

        if (localGrid) {
          removeItemFromList(ProviderFragmentDoc, id, localGrid, 'providers')
        }
      },

      optimisticResponse: {
        deleteProvider: true,
      },
    })

    if (!result.data) {
      throw new Error('Provider deletion failed')
    }

    return result.data.deleteProvider
  }

  return {
    createProvider,
    updateProvider,
    deleteProvider,
  }
}
